/**
 * Design tokens — the ADMIN dialect's typography scale.
 *
 * ADR 030 — Design Canon assigns the type scale to the admin dialect (Layer 2)
 * and, until this file, never defined it in TypeScript: the scale lived as
 * px literals in `apps/admin-web/src/styles/settings-v2.css`, which is why it
 * was the one axis with no generated output and no compile-time floor. Colour,
 * spacing and density all pass through `packages/ui`; type did not.
 *
 * These are the named steps. Each is generated into `apps/admin-web` as a
 * `--type-*` CSS var by `renderAdminTypographyCssVars` in `./css.ts`, then bound
 * to Tailwind utilities (`text-body`, `text-caption`, `text-title`, …) in
 * admin-web's `tailwind.config.ts`, so one class carries size, leading and
 * tracking together — the trio that drifts apart when they are set separately.
 *
 * **Admin only, on purpose.** This is a dialect, not foundation: it is rendered
 * to its own generated file, never into the shared `generated-tokens.css` both
 * apps import, and bidder-web's Tailwind config never learns these keys.
 * bidder-web's `--bw-*` vocabulary is the other dialect, and "unifying" the two
 * is the regression the Canon names. Same shape as `density.ts` (BL-1357).
 *
 * **Retiring the settings-v2 `--text-*` scale needs its own ADR + sign-off.**
 * ADR 030 puts that explicitly out of its own scope, so this file is the
 * mechanism, not the authority. Until that ADR is accepted the old vars stay
 * and both vocabularies are reachable; the drain is what removes them (ADR 044
 * — Must Consolidate: the alternatives must become unreachable, not merely
 * un-preferred).
 */

/**
 * A size on the scale, in px. Anything else fails to compile.
 *
 * The steps follow one rule, and the rule is the point — it is what lets the
 * scale be extended without a meeting: **to 20 the step is 2px, to 24 it is 4px,
 * above that it is 8px.** The bottom is linear because legibility at small sizes
 * tracks the absolute pixel difference, not the ratio; a geometric run through
 * that region lands on 16.8 and 20.2, which is how the scale this replaces ended
 * up with 11.5 / 12.5 / 13.5 and steps of x1.08 that no eye can separate.
 *
 * **The 2px band reached 20 on 2026-09-23 (BL-1857)**, when `titleSm` (18) was
 * added. It was 16 before that, which put a 4px jump between the largest body
 * size and the smallest heading — too coarse for a phone, where a 20px heading
 * over 14/16px body wraps a record's name to three lines at 393px. See ADR 063 —
 * Type Scale, amendment BL-1857.
 */
export type TypeSizeStep = 10 | 12 | 14 | 16 | 18 | 20 | 24 | 32 | 40;

/**
 * A line height on the scale, in px — always a multiple of 4, the same rhythm
 * `spacing.ts` sets, so a text block's box lands on the layout grid instead of
 * near it. Stated in px rather than as a ratio for the same reason: a unitless
 * 1.45 on a 14px step is 20.3px, and a half-pixel box is exactly the defect this
 * scale exists to remove.
 */
export type TypeLineHeightStep = 12 | 16 | 20 | 24 | 28 | 32 | 40 | 48;

/** One step: the three properties that must travel together. */
export interface TypeStep {
    readonly size: TypeSizeStep;
    readonly lineHeight: TypeLineHeightStep;
    /** Letter spacing in `em`, from the Inter curve below. */
    readonly tracking: string;
}

/**
 * Inter's published dynamic-tracking curve: `-0.0223 + 0.185 * e^(-0.1745 * x)`,
 * with `x` the size in px, returning `em`.
 *
 * Inter is drawn at one optical size, so it needs air at small sizes and
 * tightening at large ones. The curve is the typeface author's own answer to
 * that, which is why the `tracking` values below are derived rather than picked
 * by eye — the alternative is eight independent judgement calls nobody can
 * review.
 *
 * **The values are frozen as literals rather than computed at render time**, and
 * deliberately so: `renderAdminTypographyCssVars` must be byte-for-byte
 * reproducible for the `token-css-freshness` CI gate, and `Math.exp` is only
 * guaranteed to be *approximately* correct per ECMA-262 — its last bits may
 * differ between engines and versions. A generated stylesheet that drifts with
 * the Node build would fail that gate for no reason anyone could diagnose.
 * `typography-css.test.ts` asserts every literal against this function, so the
 * derivation stays provable without the render depending on it.
 *
 * @param sizePx - The step's size in px.
 * @returns Tracking in `em`, rounded to 4 decimal places.
 */
export function interTracking(sizePx: number): string {
    const em = -0.0223 + 0.185 * Math.exp(-0.1745 * sizePx);
    return `${Number(em.toFixed(4))}em`;
}

/**
 * The admin dialect's type steps, smallest first: `micro` 10 · `caption` 12 ·
 * `body` 14 (the base) · `bodyLg` 16 · `titleSm` 18 · `title` 20 · `titleLg` 24 ·
 * `display` 32 · `displayLg` 40.
 *
 * **Which role each step serves lives in one place only:** ADR 063 — Type Scale
 * § 1 (docs/adrs/063_ADMIN_TYPE_SCALE.md), as rewritten by its Amendment 2. A
 * copy here went stale the first time the roles changed, so this block names the
 * steps and points there rather than restating them.
 *
 * **`micro` shipped unbound and gained its role on 2026-09-22.** It exists for
 * text whose size is set by the container around it rather than by a reading
 * line — initials inside a 24px `Avatar` are the case that asked for it. Without
 * the step, Avatar's five sizes collapse from 11.5/11.5/12.5/15/24 onto
 * 12/12/12/16/24, so its three smallest circles render identical letters at
 * three different diameters; with it they read 10/10/12/16/24 and the ramp
 * survives.
 *
 * **It is not for standalone text.** 10px does not clear legibility on its own,
 * and the guard against that is the narrowness of the stated role, not a lint
 * rule. Treat "this is a bit tight, drop to micro" as the wrong answer: that is
 * exactly how this scale reached 23 sizes before BL-1841 — `--text-md` (15px, 16
 * uses) and `--text-lg` (17px, 5 uses) each entered as one reasonable exception
 * and then could not be removed quietly.
 */
export const adminType = {
    micro: { size: 10, lineHeight: 12, tracking: '0.01em' },
    caption: { size: 12, lineHeight: 16, tracking: '0.0005em' },
    body: { size: 14, lineHeight: 20, tracking: '-0.0062em' },
    bodyLg: { size: 16, lineHeight: 24, tracking: '-0.011em' },
    titleSm: { size: 18, lineHeight: 24, tracking: '-0.0143em' },
    title: { size: 20, lineHeight: 28, tracking: '-0.0167em' },
    titleLg: { size: 24, lineHeight: 32, tracking: '-0.0195em' },
    display: { size: 32, lineHeight: 40, tracking: '-0.0216em' },
    displayLg: { size: 40, lineHeight: 48, tracking: '-0.0221em' },
} as const satisfies Record<string, TypeStep>;

/**
 * The three weights, and the whole set — Inter Regular, Medium, SemiBold.
 *
 * One job each, which is what keeps a fourth from being useful: **400 is read,
 * 500 is distinguished, 600 anchors.** Body copy, table cells and field values
 * are read; labels, buttons, tabs and column headers are recognised rather than
 * read; headings, totals and key figures are what the eye lands on first.
 *
 * **700 is absent by decision, not omission.** Above 20px the hierarchy is
 * carried by size, so Bold buys nothing a step cannot; what it costs is a
 * standing "600 or 700 here?" that the codebase currently answers differently in
 * different files, plus one more Inter face in the bundle. The weights map 1:1
 * onto Tailwind's own `font-normal` / `font-medium` / `font-semibold`, so no
 * Tailwind binding is needed — what needs enforcing is that `font-bold` is not
 * reachable, and that is the lint gate's job, not this file's.
 *
 * Italic is likewise absent: no admin role needs it, and the moment it exists it
 * becomes a third way to emphasise what 500 and 600 already cover.
 */
export const adminFontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
} as const;

/**
 * The admin dialect's second face: a SYSTEM monospace stack, for identifiers
 * only (BUG-1722).
 *
 * Its one job is a value read character by character — a colour HEX, a paddle
 * number, a reference ID — where `0`/`O` and `1`/`l`/`I` must not be confused.
 * Numbers, dates, labels and money stay on Inter: `tabular-nums` already aligns
 * digits there, so mono would change their voice and buy nothing.
 *
 * A system stack, not a web font, so it adds no file to the page (the bug's
 * RAC 4); each platform's own mono face renders. Until BUG-1722 the admin
 * `--font-mono` var resolved to Inter and every `ds-mono` call site was a no-op.
 */
export const adminFontFamily = {
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
} as const;

/** The shape `renderAdminTypographyCssVars` reads — widened so tests can substitute a step. */
export type AdminTypeTokens = { [K in keyof typeof adminType]: TypeStep };
